import React from 'react';
import PropTypes from 'prop-types';
import AvailableLanguageLink from '../AvailableLanguageLink';
import { Link } from 'src/components/Inputs';
import Image from 'src/components/Image';

const propTypes = {
  id: PropTypes.string,
  availableLanguages: PropTypes.arrayOf(
    PropTypes.shape({
      href: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ),
};

const NlVersion = ({ availableLanguages }) => {
  return (
    <>
      <h4 id='privacyverklaring-voor-sollicitanten'>
        Privacyverklaring voor sollicitanten (wereldwijd)
      </h4>

      <p>Beschikbare talen:</p>
      <ul>
        {availableLanguages.map((language, i) => {
          return (
            <AvailableLanguageLink
              key={`language-${i}`}
              href={language.href}
              label={language.label}
            />
          );
        })}
      </ul>
      <br />
      <br />

      <p>Ingangsdatum: 1 Februari 24</p>
      <p>
        Bij Plaid begrijpen we het belang van privacy en transparantie. Het doel
        van deze privacyverklaring voor sollicitanten (“verklaring”) is uit te
        leggen hoe Plaid Inc. en haar dochterondernemingen, waaronder Plaid
        Financial Ltd. en Plaid, B.V. (gezamenlijk “Plaid”, “wij”, “onze”, of
        “ons”), persoonsgegevens en informatie van en over potentiële
        sollicitanten (“u” of “uw”) verzamelen, gebruiken en doorgeven voor
        functies bij Plaid (“sollicitanteninformatie”).
      </p>
      <p>
        Houd er rekening mee dat als u uw cv of andere sollicitanteninformatie
        via onze website indient, we ook informatie over u kunnen verzamelen en
        verwerken in verband met uw toegang tot of gebruik van de website van
        Plaid. Aanvullende informatie over ons gebruik van uw gegevens vindt u
        in onze Privacyverklaring. Meer informatie over de soorten cookies die
        we op onze website gebruiken, waarom we ze gebruiken en hoe u het
        gebruik van cookies kunt beheren of beperken, vindt u in ons{' '}
        <Link href='#cookiebeleid'>
          <a>Cookiebeleid</a>
        </Link>
        .
      </p>
      <p>
        <em>Ga naar sectie:</em>
      </p>
      <p>
        <Link href='#onze-gegevenspraktijken-privacyverklaring-voor-sollicitanten'>
          <a data-smooth-scroll data-offset='80'>
            Onze gegevenspraktijken
          </a>
        </Link>
      </p>

      <ul data-smooth-scroll data-offset='80'>
        <li>
          <p>
            <Link href='#informatie-die-we-verzamelen-privacyverklaring-voor-sollicitanten'>
              <a>Informatie die we verzamelen</a>
            </Link>
          </p>
        </li>
        <li>
          <p>
            <Link href='#hoe-we-uw-informatie-gebruiken-privacyverklaring-voor-sollicitanten'>
              <a>Hoe we uw informatie gebruiken</a>
            </Link>
          </p>
        </li>
        <li>
          <p>
            <Link href='#hoe-we-uw-informatie-openbaar-maken-privacyverklaring-voor-sollicitanten'>
              <a>Hoe we uw informatie openbaar maken</a>
            </Link>
          </p>
        </li>
        <li>
          <p>
            <Link href='#onze-wettelijke-grondslagen-voor-verwerking-privacyverklaring-voor-sollicitanten'>
              <a>
                Onze wettelijke grondslagen voor verwerking (alleen voor
                sollicitanten uit het VK en de EER)
              </a>
            </Link>
          </p>
        </li>
        <li>
          <p>
            <Link href='#onze-bewaarpraktijken-privacyverklaring-voor-sollicitanten'>
              <a>Onze bewaarpraktijken</a>
            </Link>
          </p>
        </li>
      </ul>

      <p>
        <Link href='#overige-details-privacyverklaring-voor-sollicitanten'>
          <a data-smooth-scroll data-offset='80'>
            Overige details
          </a>
        </Link>
      </p>

      <ul data-smooth-scroll data-offset='80'>
        <li>
          <p>
            <Link href='#internationale-gegevensoverdrachten-privacyverklaring-voor-sollicitanten'>
              <a>Internationale gegevensoverdrachten</a>
            </Link>
          </p>
        </li>
        <li>
          <p>
            <Link href='#gegevensbeschermingsrechten-van-sollicitanten-in-californië'>
              <a>Gegevensbeschermingsrechten van sollicitanten in Californië</a>
            </Link>
          </p>
        </li>
        <li>
          <p>
            <Link href='#gegevensbeschermingsrechten-voor-sollicitanten-privacyverklaring-voor-sollicitanten'>
              <a>
                Gegevensbeschermingsrechten voor sollicitanten in het VK, de EER
                en Canada
              </a>
            </Link>
          </p>
        </li>
        <li>
          <p>
            <Link href='#wijzigingen-in-deze-verklaring-privacyverklaring-voor-sollicitanten'>
              <a>Wijzigingen in deze verklaring</a>
            </Link>
          </p>
        </li>
        <li>
          <p>
            <Link href='#contact-opnemen-met-plaid-privacyverklaring-voor-sollicitanten'>
              <a>Contact opnemen met Plaid</a>
            </Link>
          </p>
        </li>
      </ul>
      <br />
      <br />

      <h4 id='onze-gegevenspraktijken-privacyverklaring-voor-sollicitanten'>
        Onze gegevenspraktijken
      </h4>

      <h5 id='informatie-die-we-verzamelen-privacyverklaring-voor-sollicitanten'>
        Informatie die we verzamelen
      </h5>
      <p>
        We verzamelen sollicitanteninformatie die worden verstrekt door (1) u,
        ook via de carrièrepagina van Plaid (beschikbaar op
        https://plaid.com/careers/), (2) derden, zoals aanbieders van
        antecedentenonderzoek, wervingsconsulenten of professionele
        wervingsbureaus, of (3) Plaid zelf via sollicitantenonderzoek. We kunnen
        ook sollicitanteninformatie afleiden door een profiel aan te maken dat
        uw arbeidsvoorkeuren, eigenschappen, gedrag, intelligentie, capaciteiten
        en bekwaamheid weergeeft, bijvoorbeeld via notities die zijn gemaakt als
        onderdeel van de sollicitatieprocedure.
      </p>
      <p>
        We verzamelen de volgende categorieën sollicitanteninformatie over
        sollicitanten die we overwegen voor vacatures:
      </p>
      <ul>
        <li>
          <p>
            <u>identificatiegegevens en contactinformatie</u>, waaronder uw
            volledige naam, adres, telefoonnummer, e-mailadres of andere
            contactinformatie;
          </p>
        </li>
        <li>
          <p>
            <u>professionele of werkgerelateerde informatie</u>, inclusief
            informatie in uw cv, uw werkgeschiedenis, certificeringen,
            professionele licenties en andere ervaring, en contactgegevens van
            uw voormalige/huidige werkgever (wanneer dergelijke gegevens uw
            persoonsgegevens bevatten);
          </p>
        </li>
        <li>
          <p>
            <u>opleidingsinformatie</u>, waaronder informatie over uw diploma’s
            en training, transcripties en academische prestaties;
          </p>
        </li>
        <li>
          <p>
            <u>sociale netwerken en soortgelijke informatie</u>, zoals sociale
            media en openbare profielinformatie, waaronder URL’s met betrekking
            tot uw profiel op LinkedIn, Twitter, GitHub en Portfolio en uw
            persoonlijke website;
          </p>
        </li>
        <li>
          <p>
            <u>
              in bepaalde regio’s (bijvoorbeeld de VS) kunnen we demografische
              en andere gevoelige informatie verzamelen (bijv. ras, gender,
              veteranenstatus, etniciteit, seksuele geaardheid en
              handicapstatus);
            </u>
            ; en
          </p>
        </li>
        <li>
          <p>
            <u>andere informatie die u indient</u>, inclusief aanvullende
            informatie verstrekt in een sollicitatieformulier, begeleidende
            brief of ander werkproduct.
          </p>
        </li>
      </ul>

      <p>
        Als uw sollicitatie vordert, verzamelen we ook de volgende
        sollicitanteninformatie:
      </p>
      <ul>
        <li>
          <p>
            <u>
              informatie die wordt verzameld als onderdeel van de
              sollicitatieprocedure
            </u>
            , bijvoorbeeld notities die worden gemaakt van uw
            sollicitatiegesprek (wanneer deze uw persoonsgegevens bevatten) en
            persoonsgegevens die zijn opgenomen in elk thuisproject;
          </p>
        </li>
        <li>
          <p>
            <u>informatie die nodig is om uw sollicitatiegesprek te plannen</u>,
            inclusief identificatiegegevens zoals uw adres, uw bekende
            reizigersnummer en eventuele frequente reizigersgegevens;
          </p>
        </li>
        <li>
          <p>
            <u>audio- en visuele informatie</u>, waaronder foto’s voor
            werkprofielen en beveiligingsbeelden die worden verzameld op het
            terrein van Plaid;
          </p>
        </li>
        <li>
          <p>
            <u>
              informatie over elektronische netwerkactiviteiten en
              gebruiksgegevens
            </u>
            , inclusief uw gebruik van apparatuur, apparaten, software en andere
            activa van Plaid.
          </p>
        </li>
        <li>
          <p>
            <u>
              informatie over de verwachtingen ten aanzien van het dienstverband
            </u>
            , inclusief, voor zover deze uw persoonsgegevens bevatten, het soort
            werk dat u zoekt, verwachtingen ten aanzien van vergoedingen,
            bereidheid om te verhuizen en andere functievoorkeuren;
          </p>
        </li>
        <li>
          <p>
            <u>
              (indien en voor zover relevant voor een specifieke functie)
              informatie die nodig is om een antecedentenonderzoek uit te voeren
            </u>
            , bijvoorbeeld informatie die van u is verkregen of door middel van
            referentie- en antecedentenonderzoeken, onder voorbehoud van
            eventuele verdere toestemmingen of kennisgevingen die vereist en/of
            toegestaan zijn door de toepasselijke wetgeving, meestal door middel
            van het verstrekken van een officieel certificaat van goed gedrag
            (bijvoorbeeld in Nederland: Verklaring omtrent gedrag of VOG); en
          </p>
        </li>
        <li>
          <p>
            <u>informatie die nodig is om het dienstverband te starten</u>,
            inclusief bewijs van geschiktheid om in een bepaald land te werken
            (zoals een paspoort of visum), demografische informatie zoals uw
            geboortedatum en -plaats en staatsburgerschap, identificatiegegevens
            zoals uw burgerservicenummer (indien vereist en/of toegestaan door
            de wet), nationaal verzekeringsnummer, andere door de overheid
            uitgegeven identificatiemiddelen, belastingidentificatiegegevens en
            gerelateerde belastinginformatie (indien vereist en/of toegestaan
            door de wet) en bankrekeningnummers en gerelateerde informatie,
            informatie over het in aanmerking komen voor secundaire
            arbeidsvoorwaarden en andere informatie die u verstrekt met het oog
            op het begin van het dienstverband of de uitvoering van een
            arbeidsovereenkomst.
          </p>
        </li>
      </ul>

      <h5 id='hoe-we-uw-informatie-gebruiken-privacyverklaring-voor-sollicitanten'>
        Hoe we uw informatie gebruiken
      </h5>
      <p>
        We gebruiken de sollicitanteninformatie die over u zijn verzameld om uw
        sollicitatie te verwerken en om te voldoen aan wettelijke verplichtingen
        die we hebben met betrekking tot uw sollicitatie, inclusief het
        bevestigen van uw geschiktheid om op een bepaalde locatie te werken.
      </p>
      <p>
        We gebruiken uw sollicitatiegegevens ook voor de volgende doeleinden:
      </p>
      <ul>
        <li>
          <p>
            <u>het beheren en verwerken van uw sollicitatie</u>, inclusief het
            verifiëren van uw identificatie, ervaring en andere informatie die u
            indient en, als uw sollicitatie vordert, alle informatie over
            sollicitatiegesprekken en antecedentenonderzoek;
          </p>
        </li>
        <li>
          <p>
            <u>met u te communiceren over uw sollicitatie</u>, inclusief contact
            met u opnemen via telefoon, e-mail of sociale-mediaplatforms over uw
            sollicitatie en, als uw sollicitatie vordert, het coördineren van
            sollicitatiegesprekken en antecedentenonderzoeken;
          </p>
        </li>
        <li>
          <p>
            <u>
              beoordelen en evalueren van uw geschiktheid voor de functie waarop
              u hebt gesolliciteerd, of voor andere vacatures
            </u>
            , inclusief het verifiëren van uw identificatie, ervaring en andere
            informatie die u indient en, als uw sollicitatie vordert, alle
            sollicitatiegesprekinformatie en antecedentenonderzoeksinformatie;
          </p>
        </li>
        <li>
          <p>
            <u>
              uitvoeren van referentie- en antecedentenonderzoeken, inclusief
              strafbladcontroles, als onderdeel van uw sollicitatie
            </u>
            , onderworpen aan de toepasselijke wetgeving, inclusief het
            verifiëren van uw identificatie, ervaring en andere informatie die u
            indient en, als uw sollicitatie vordert,
            sollicitatiegesprekinformatie en antecedentenonderzoeksinformatie;
          </p>
        </li>
        <li>
          <p>
            <u>
              het naleven van toepasselijke wetten en arbeidsgerelateerde
              vereisten
            </u>
            , waaronder het verifiëren van identificatiegegevens en informatie
            die nodig is om een dienstverband te initiëren, voor doeleinden
            zoals het bevestigen van de mogelijkheid om legaal te werken op een
            specifieke locatie, het opzetten van een salarisadministratie,
            inhoudingen en secundaire arbeidsvoorwaarden en het voldoen aan
            wettelijke rapportagevereisten;
          </p>
        </li>
        <li>
          <p>
            <u>het verbeteren van onze wervingsprocedure</u>, zoals toegestaan
            door de toepasselijke wetgeving en alleen in bepaalde regio’s (zoals
            de VS), inclusief door het genereren van analyses met behulp van
            sollicitanteninformatie om te zorgen dat we een diverse reeks
            sollicitanten werven;
          </p>
        </li>
        <li>
          <p>
            <u>u informeren over carrièremogelijkheden</u>, waaronder andere
            vacatures waarvoor u eventueel in aanmerking komt; en
          </p>
        </li>
        <li>
          <p>
            <u>voor enig ander vermeld doel met uw toestemming</u>.
          </p>
        </li>
      </ul>
      <p>
        Houd er rekening mee dat als u een dienstverband bij Plaid accepteert,
        uw sollicitanteninformatie deel zullen uitmaken van uw arbeidsdossier en
        zullen worden gebruikt voor arbeidsdoeleinden in overeenstemming met de
        toepasselijke wetgeving en elke toepasselijke privacyverklaring voor
        personeel. Als u echter niet wordt geaccepteerd voor een functie, kunnen
        we uw sollicitatie, inclusief relevante sollicitanteninformatie, nog
        steeds bewaren zodat we u kunnen overwegen voor andere
        carrièremogelijkheden bij Plaid en op voorwaarde dat, indien vereist
        door de toepasselijke wetgeving, we uw voorafgaande toestemming hebben
        verkregen voor een dergelijke langere bewaring van uw persoonsgegevens.
        Als u niet wilt dat uw informatie wordt bewaard indien u geen functie
        wordt aangeboden, waar van toepassing, raadpleeg dan de volgende secties
        hieronder voor opties die voor u beschikbaar kunnen zijn:{' '}
        <Link href='#gegevensbeschermingsrechten-van-sollicitanten-in-californië'>
          <a>Gegevensbeschermingsrechten van sollicitanten in Californië</a>
        </Link>{' '}
        en{' '}
        <Link href='#gegevensbeschermingsrechten-voor-sollicitanten-privacyverklaring-voor-sollicitanten'>
          <a>
            Gegevensbeschermingsrechten van Britse, EER- en Canadese
            sollicitanten
          </a>
        </Link>
        .
      </p>
      <p>
        Plaid gebruikt uw persoonsgegevens niet voor geautomatiseerde
        individuele besluitvorming (inclusief profilering).
      </p>

      <h5 id='hoe-we-uw-informatie-openbaar-maken-privacyverklaring-voor-sollicitanten'>
        Hoe we uw informatie openbaar maken
      </h5>
      <p>
        We maken uw persoonsgegevens als volgt of zoals anderszins beschreven in
        deze verklaring aan anderen bekend:
      </p>
      <ul>
        <li>
          <p>
            <u>
              aan Plaid en onze dochterondernemingen en gelieerde entiteiten
            </u>
            , aangezien de specifieke entiteit waarbij u solliciteert deel
            uitmaakt van een bredere groep bedrijven met hoofdkantoor in de
            Verenigde Staten en kantoren in verschillende rechtsgebieden die
            personeelszaken en IT-systemen delen voor het verwerken van uw
            sollicitatie, het beoordelen van uw geschiktheid voor de functie en
            het initiëren van een dienstverband (indien van toepassing);
          </p>
        </li>
        <li>
          <p>
            <u>aan onze gegevensverwerkers en andere dienstverleners</u>,
            waaronder onze leveranciers van diensten voor gegevensanalyse en
            antecedentenonderzoek en wervingsbureaus, in verband met de diensten
            die zij voor ons of namens ons verrichten. Indien vereist, zijn deze
            dienstverleners, partners of verkopers die namens ons handelen
            onderworpen aan contractuele verplichtingen om passende technische
            en organisatorische maatregelen te implementeren om de
            sollicitanteninformatie te beschermen, en om deze alleen volgens de
            instructies te verwerken.
          </p>
        </li>
        <li>
          <p>
            <u>
              aan wetshandhavingsinstanties, overheidsinstanties, regelgevende
              instanties en andere derden
            </u>
            , zoals rechtbanken en andere autoriteiten, onafhankelijke externe
            adviseurs en interne nalevings- en onderzoeksteams, als we denken
            dat een dergelijke openbaarmaking in overeenstemming is met of op
            een andere manier vereist is door toepasselijke wet- en regelgeving
            of juridische procedures;
          </p>
        </li>
        <li>
          <p>
            <u>
              in verband met, of tijdens de onderhandelingen over, een fusie,
              verkoop van bedrijfsmiddelen, consolidatie of herstructurering,
              financiering of overname van het gehele of een deel van ons
              bedrijf door of in een ander bedrijf, voor zover toegestaan
            </u>
            , door de toepasselijke wetgeving;
          </p>
        </li>
        <li>
          <p>
            <u>indien nodig om Plaid te beschermen</u>, inclusief om potentiële,
            dreigende of feitelijke juridische vorderingen vast te stellen, uit
            te oefenen of te verdedigen;
          </p>
        </li>
        <li>
          <p>
            <u>
              om een contract met u af te dwingen of als wij van mening zijn dat
              uw acties niet in overeenstemming zijn met onze beleidslijnen
            </u>
            ;
          </p>
        </li>
        <li>
          <p>
            <u>
              om te reageren op vorderingen, om onze rechten of de rechten van
              een derde partij te beschermen, om de veiligheid van een persoon
              te beschermen of om illegale activiteiten te voorkomen
            </u>
            ; of
          </p>
        </li>
        <li>
          <p>
            <u>
              voor enig ander vermeld doel met uw toestemming of op uw
              aanwijzing
            </u>
            .
          </p>
        </li>
      </ul>
      <p>
        We kunnen ook geaggregeerde, geanonimiseerde of niet-identificeerbaar
        gemaakte informatie gebruiken of openbaar maken die niet redelijkerwijs
        kan worden gebruikt om u te identificeren, voor zover toegestaan volgens
        de toepasselijke wetgeving.
      </p>

      <h5 id='onze-wettelijke-grondslagen-voor-verwerking-privacyverklaring-voor-sollicitanten'>
        Onze wettelijke grondslagen voor verwerking (alleen voor sollicitanten
        uit het VK en de EER)
      </h5>
      <p>
        Voor sollicitanten in het Verenigd Koninkrijk (“VK”) en de Europese
        Economische Ruimte (“EER”), hangt onze rechtsgrond voor het verzamelen,
        gebruiken en doorgeven van sollicitanteninformatie af van de betreffende
        informatie en de context waarin we deze hebben verzameld of verwerkt.
        Normaal gesproken verzamelen en verwerken we echter in het algemeen
        alleen sollicitanteninformatie wanneer:
      </p>
      <p>
        (a) we moeten voldoen aan wettelijke verplichtingen die op ons van
        toepassing zijn (zoals toepasselijke immigratie- en/of arbeidswet- en
        regelgeving);
      </p>
      <p>
        (b) we de informatie nodig hebben om te voldoen aan onze
        verantwoordelijkheden in een feitelijke of toekomstige overeenkomst met
        u (zoals een arbeidsovereenkomst of een aanbod van werk als u in
        aanmerking komt voor een dienstverband);
      </p>
      <p>
        (c) de verwerking noodzakelijk is voor onze rechtmatige belangen en niet
        wordt opgeheven door uw belangen op het gebied van gegevensbescherming
        of fundamentele rechten en vrijheden (zoals het garanderen van een
        veilige werkomgeving voor al onze teamleden en ter verzekering van de
        betrouwbaarheid van onze sollicitanten en voor het anderszins beheren en
        verwerken van uw sollicitatie, of om met u over uw sollicitatie te
        communiceren); en
      </p>
      <p>
        (d) we uw toestemming hebben om dit te doen (in gevallen waarin uw
        toestemming geheel vrijelijk wordt gegeven).
      </p>
      <p>
        Wanneer het ons wettelijk is toegestaan of we verplicht zijn om speciale
        categorieën gegevens te verzamelen en te verwerken (bijvoorbeeld ras),
        verwerken we dergelijke informatie alleen:
      </p>
      <ul>
        <li>
          <p>
            wanneer we dit redelijkerwijs moeten doen om te voldoen aan onze
            wettelijke verplichtingen als toekomstige werkgever;
          </p>
        </li>
        <li>
          <p>
            wanneer we dit moeten doen om op de hoogte te worden gebracht van uw
            arbeidscapaciteit;
          </p>
        </li>
        <li>
          <p>
            wanneer we dit moeten doen om de vitale belangen van u of een andere
            persoon te beschermen (en we uw toestemming niet kunnen verkrijgen);
            of
          </p>
        </li>
        <li>
          <p>
            wanneer u ons uw toestemming hebt gegeven (in gevallen waarin uw
            toestemming geheel vrijelijk wordt gegeven).
          </p>
        </li>
      </ul>

      <p>
        Voor zover wij ons baseren op toestemming om sollicitanteninformatie te
        verzamelen en te verwerken, hebt u het recht om uw toestemming te
        weigeren of om uw toestemming op elk moment in te trekken door contact
        met ons op te nemen via{' '}
        <Link href='mailto:people-privacy@plaid.com'>
          <a>people-privacy@plaid.com</a>
        </Link>
        . Het intrekken van uw toestemming heeft echter geen invloed op de
        rechtmatigheid van de verwerking die wij voor uw intrekking hebben
        uitgevoerd, noch op de verwerking van uw persoonsgegevens voor andere
        doeleinden, uitgevoerd op grond van andere rechtmatige
        verwerkingsgronden dan toestemming.
      </p>

      <h5 id='onze-bewaarpraktijken-privacyverklaring-voor-sollicitanten'>
        Onze bewaarpraktijken
      </h5>
      <p>
        We bewaren sollicitanteninformatie niet langer dan nodig is om te
        voldoen aan het doel waarvoor deze is verzameld en gebruikt, zoals
        beschreven in deze verklaring, tenzij een langere bewaartermijn is
        vereist of toegestaan krachtens de toepasselijke wetgeving. Echter,
        zoals hierboven vermeld, als u een dienstverband bij Plaid accepteert,
        wordt uw sollicitanteninformatie een deel van uw arbeidsdossier en wordt
        gebruikt voor arbeidsdoeleinden in overeenstemming met de toepasselijke
        wetgeving en eventuele toepasselijke privacyverklaring voor personeel.
      </p>

      <h4 id='overige-details-privacyverklaring-voor-sollicitanten'>
        Overige details
      </h4>

      <h5 id='internationale-gegevensoverdrachten-privacyverklaring-voor-sollicitanten'>
        Internationale gegevensoverdrachten
      </h5>
      <p>
        We zijn internationaal actief en voeren daarom overdracht van
        sollicitanteninformatie uit over internationale grenzen, onder andere
        van het VK, de EER en/of Canada naar de Verenigde Staten voor verwerking
        en opslag. Voor zover sollicitanteninformatie wordt overgedragen van het
        VK of de EER naar gebieden/landen waarvoor de Europese Commissie en/of
        de minister van binnenlandse zaken in het VK (in voorkomend geval) niet
        heeft vastgesteld dat het wettelijke kader in dat gebied/land adequate
        bescherming biedt voor de rechten en vrijheden van personen voor hun
        persoonsgegevens, dragen we dergelijke gegevens over in overeenstemming
        met de toepasselijke wetgeving voor gegevensbescherming, onder meer door
        het gebruik van door de Europese Commissie en/of de minister van
        binnenlandse zaken in het VK goedgekeurde (in voorkomend geval)
        standaardcontractbepalingen (indien van toepassing). U kunt om een kopie
        van deze standaardcontractbepalingen vragen door contact op te nemen
        zoals hieronder uiteengezet.
      </p>
      <p>
        Daarnaast zetten we ons in om ervoor te zorgen dat we voldoen aan alle
        aanvullende voorschriften of richtlijnen van het Europees Hof van
        Justitie, de wetgeving voor gegevensbescherming en/of toezichthoudende
        autoriteiten met betrekking tot de overdracht van persoonsgegevens.
      </p>

      <h5 id='gegevensbeschermingsrechten-van-sollicitanten-in-californië'>
        Gegevensbeschermingsrechten van sollicitanten in Californië
      </h5>
      <p>
        De California Consumer Privacy Act of “CCPA”, zoals gewijzigd door de
        California Privacy Rights Act, biedt sollicitanten die in Californië
        wonen bepaalde rechten met betrekking tot hun sollicitanteninformatie.
        Als u inwoner van Californië bent, is dit gedeelte op u van toepassing.
      </p>
      <p>
        We verzamelen of hebben in de afgelopen 12 maanden de categorieën
        sollicitanteninformatie verzameld die in de onderstaande tabel worden
        vermeld uit de bronnen die worden geïdentificeerd in het gedeelte
        “Informatie die we verzamelen”. Voor details over de precieze
        gegevenspunten die we verzamelen of in de afgelopen 12 maanden hebben
        verzameld, raadpleeg dan het gedeelte “Informatie die we verzamelen”
        hierboven. We gebruiken en maken de sollicitanteninformatie bekend voor
        de zakelijke en commerciële doeleinden die in de volgende tabel worden
        vermeld en we maken de sollicitanteninformatie bekend aan de ontvangers
        die ook in de volgende tabel worden vermeld.
      </p>
      <Image
        src='/assets/img/legal/candidate-privacy-notice/candidate-information-table-nl@2x.png'
        alt='Kandidaat Informatie tabel'
        isLazy={false}
      />
      <p>
        We bewaren de categorieën sollicitanteninformatie in overeenstemming met
        de hierboven beschreven bewaarpraktijken in{' '}
        <Link href='#onze-bewaarpraktijken-privacyverklaring-voor-sollicitanten'>
          <a>Onze bewaarpraktijken</a>
        </Link>
        .
      </p>
      <p>
        Plaid (1) “verkoopt” geen sollicitanteninformatie en “geeft” deze niet
        “door” en (2) gebruikt of maakt gevoelige persoonlijke informatie niet
        openbaar voor enig ander doel dan wat wordt gespecificeerd in
        CCPA-sectie 1798.121(a) (zoals deze termen worden gedefinieerd door de
        CCPA).
      </p>
      <p>
        Onder voorbehoud van bepaalde beperkingen hebt u het recht om (a) te
        verzoeken om meer te weten te komen over de bronnen van en de
        categorieën en specifieke stukjes sollicitanteninformatie die we
        verzamelen, gebruiken en openbaar maken en de categorieën derden aan wie
        we dergelijke informatie bekendmaken, (b) te verzoeken om verwijdering
        van sollicitanteninformatie, (c) te verzoeken om correctie van onjuiste
        sollicitanteninformatie, en (d) niet te worden gediscrimineerd voor het
        uitoefenen van deze rechten. U kunt deze verzoeken indienen door een
        e-mail te sturen naar people-privacy@plaid.com, dit{' '}
        <Link href='https://my.plaid.com/data-subject-request-form-nl/'>
          <a>webformulier</a>
        </Link>{' '}
        in te vullen of te bellen naar +1 (415) 799 - 1354. We zullen uw verzoek
        verifiëren door u te vragen informatie te verstrekken met betrekking tot
        uw recente interacties met ons, zoals uw telefoonnummer en e-mailadres.
        We discrimineren u niet als u uw rechten krachtens de CCPA uitoefent.
      </p>
      <p>
        Als we uw verzoek ontvangen van een gemachtigde vertegenwoordiger die
        geen geldige volmacht verstrekt, kunnen we de gemachtigde
        vertegenwoordiger vragen om bewijs te leveren dat u de vertegenwoordiger
        ondertekende toestemming hebt gegeven om het verzoek in te dienen om
        namens u rechten uit te oefenen. Bij gebrek aan een geldige volmacht
        kunnen we u ook vragen om uw eigen identiteit rechtstreeks bij ons te
        verifiëren of aan ons te bevestigen dat u anderszins de gemachtigde
        vertegenwoordiger toestemming hebt gegeven om het verzoek in te dienen.
        Als u een gemachtigde vertegenwoordiger bent die een verzoek wil
        indienen, stuur dan een e-mail naar{' '}
        <Link href='mailto:people-privacy@plaid.com'>
          <a>people-privacy@plaid.com</a>
        </Link>
        .
      </p>

      <h5 id='gegevensbeschermingsrechten-voor-sollicitanten-privacyverklaring-voor-sollicitanten'>
        Gegevensbeschermingsrechten voor sollicitanten in het VK, de EER en
        Canada
      </h5>
      <p>
        Krachtens de toepasselijke wetgeving hebt u bepaalde rechten met
        betrekking tot uw sollicitanteninformatie. Deze rechten kunnen per land
        en provincie verschillen.
      </p>
      <p>
        Onderhevig aan bepaalde beperkingen en uitzonderingen, omvatten die
        rechten het recht om: (i) te weten of we persoonsgegevens over u
        verwerken; (ii) toegang te vragen tot of rectificatie van de
        persoonsgegevens die we over u verwerken; (iii) bezwaar te maken tegen,
        of te verzoeken dat we de verwerking van uw persoonsgegevens beperken;
        (iv) ons te verzoeken persoonsgegevens over u te verwijderen; (v) te
        verzoeken dat wij de persoonsgegevens die u aan ons hebt verstrekt,
        verstrekken in een gestructureerd, algemeen gebruikt en machinaal
        leesbaar formaat, voor verzending naar een andere
        verwerkingsverantwoordelijke; (vi) uw toestemming op elk moment in te
        trekken wanneer onze verwerking gebaseerd is op uw toestemming; en (vii)
        een klacht in te dienen bij uw lokale toezichthoudende autoriteit (als u
        in het VK bent, raadpleeg dan deze{' '}
        <Link href='https://ico.org.uk/global/contact-us/'>
          <a>pagina</a>
        </Link>{' '}
        voor contactinformatie voor het Britse Information Commissioner’s Office
        en als u in de EER bent, raadpleeg dan deze{' '}
        <Link href='https://edpb.europa.eu/about-edpb/board/members_en'>
          <a>pagina</a>
        </Link>{' '}
        voor contactinformatie voor toezichthoudende instanties van de EU).
      </p>
      <p>
        Als u uw rechten wilt uitoefenen, neem dan contact met ons op via de
        onderstaande contactinformatie.
      </p>
      <p>
        We geven u graag op uw verzoek een meer gedetailleerd overzicht van onze
        relevante verwerkingsdoeleinden (evenals de gerelateerde categorieën
        gegevens, bewaartermijnen en wettelijke verwerkingsgronden).
      </p>

      <h5 id='wijzigingen-in-deze-verklaring-privacyverklaring-voor-sollicitanten'>
        Wijzigingen in deze verklaring
      </h5>
      <p>
        We kunnen deze privacyverklaring voor sollicitanten van tijd tot tijd
        wijzigen. Als we wijzigingen aanbrengen, veranderen we de ingangsdatum
        bovenaan deze verklaring. Bovendien, als een van de wijzigingen van
        wezenlijk belang is en van invloed is op uw sollicitanteninformatie,
        sturen we u ook een kopie via e-mail.
      </p>

      <h5 id='contact-opnemen-met-plaid-privacyverklaring-voor-sollicitanten'>
        Contact opnemen met Plaid
      </h5>
      <p>
        Als u vragen hebt over deze privacyverklaring voor sollicitanten, onze
        privacypraktijken of de sollicitanteninformatie die we over u
        verzamelen, of als u verzoeken hebt met betrekking tot dergelijke
        sollicitanteninformatie, kunt u contact met ons opnemen via{' '}
        <Link href='mailto:people-privacy@plaid.com'>
          <a>people-privacy@plaid.com</a>
        </Link>{' '}
        of per post via:
      </p>
      <p>
        <em>
          Als u solliciteert naar een functie bij Plaid Inc. (gevestigd in de
          Verenigde Staten):
        </em>
      </p>
      <p>
        Plaid Inc. <br />
        Attn: Legal <br />
        PO Box 7775 #35278 <br />
        San Francisco, California 94120-7775, U.S.A.
      </p>
      <p>
        <em>
          Als u solliciteert naar een functie bij Plaid Financial Ltd.
          (gevestigd in het Verenigd Koninkrijk):
        </em>
      </p>
      <p>
        Plaid Financial Ltd. <br />
        Attn: Legal <br />
        New Penderel House, 4th Floor <br />
        283-288 High Holborn
        <br />
        London, United Kingdom, WC1V 7HP
      </p>
      <p>
        <em>
          Als u solliciteert naar een functie bij Plaid, B.V. (gevestigd in
          Nederland):
        </em>
      </p>
      <p>
        Plaid, B.V. <br />
        t.a.v.: Juridische afdeling <br />
        Muiderstraat 1 <br />
        1011 PZ Amsterdam, Nederland
      </p>

      <hr className='section-divider' />
    </>
  );
};

propTypes.NlVersion = PropTypes;
export default NlVersion;
